import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import {
  Outlet,
  Link,
  createRootRouteWithContext,
  useRouter,
  HeadContent,
  Scripts,
} from "@tanstack/react-router";

import appCss from "../styles.css?inline";
import { lazy, Suspense, useEffect, useState } from "react";
const AccessibilityWidget = lazy(() =>
  import("@/components/AccessibilityWidget").then((m) => ({ default: m.AccessibilityWidget }))
);

function NotFoundComponent() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-7xl font-bold text-foreground">404</h1>
        <h2 className="mt-4 text-xl font-semibold text-foreground">Page not found</h2>
        <p className="mt-2 text-sm text-muted-foreground">
          The page you're looking for doesn't exist or has been moved.
        </p>
        <div className="mt-6">
          <Link
            to="/"
            className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
          >
            Go home
          </Link>
        </div>
      </div>
    </div>
  );
}

function ErrorComponent({ error, reset }: { error: Error; reset: () => void }) {
  console.error(error);
  const router = useRouter();

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-xl font-semibold tracking-tight text-foreground">
          This page didn't load
        </h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Something went wrong on our end. You can try refreshing or head back home.
        </p>
        <div className="mt-6 flex flex-wrap justify-center gap-2">
          <button
            onClick={() => {
              router.invalidate();
              reset();
            }}
            className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
          >
            Try again
          </button>
          <a
            href="/"
            className="inline-flex items-center justify-center rounded-md border border-input bg-background px-4 py-2 text-sm font-medium text-foreground transition-colors hover:bg-accent"
          >
            Go home
          </a>
        </div>
      </div>
    </div>
  );
}

import simpletechLogo from "@/assets/simpletech-logo-transparent.png";

import { SITE_URL } from "@/lib/seo";
const BASE_URL = SITE_URL;

export const Route = createRootRouteWithContext<{ queryClient: QueryClient }>()({
  head: () => ({
    meta: [
      { charSet: "utf-8" },
      { name: "google-site-verification", content: "gmlIZVnv_vfJyU4qYnVy9Sy5TJoub9EFC5ArRV1ifAk" },
      { name: "viewport", content: "width=device-width, initial-scale=1" },
      { title: "SimpleTech" },
      { name: "description", content: "IT support, cybersecurity, and managed services for businesses." },
      { name: "author", content: "SimpleTech" },
      { property: "og:title", content: "SimpleTech" },
      { property: "og:description", content: "IT support, cybersecurity, and managed services for businesses." },
      { property: "og:type", content: "website" },
      { property: "og:url", content: BASE_URL },
      { property: "og:site_name", content: "SimpleTech" },
      { name: "twitter:card", content: "summary" },
      { name: "twitter:site", content: "@SimpleTech" },
      { name: "twitter:title", content: "SimpleTech" },
      { name: "twitter:description", content: "IT support, cybersecurity, and managed services for businesses." },
      { property: "og:image", content: "https://pub-bb2e103a32db4e198524a2e9ed8f35b4.r2.dev/6211c2dd-4ff4-43dc-8eae-d8173dd51b27/id-preview-549346a3--ab80396c-1b33-4b69-9aed-1331e80ae8f8.lovable.app-1779398698981.png" },
      { name: "twitter:image", content: "https://pub-bb2e103a32db4e198524a2e9ed8f35b4.r2.dev/6211c2dd-4ff4-43dc-8eae-d8173dd51b27/id-preview-549346a3--ab80396c-1b33-4b69-9aed-1331e80ae8f8.lovable.app-1779398698981.png" },
    ],
    links: [
      {
        rel: "icon",
        type: "image/png",
        href: simpletechLogo,
      },
      { rel: "preconnect", href: "https://fonts.gstatic.com", crossOrigin: "anonymous" },
    ],
    scripts: [
      {
        children: `window.dataLayer=window.dataLayer||[];function gtag(){dataLayer.push(arguments);}window.gtag=gtag;try{var c=JSON.parse(localStorage.getItem('simpletech-cookie-consent-v1')||'{}');var a=c.analytics?'granted':'denied';var m=c.marketing?'granted':'denied';gtag('consent','default',{ad_storage:m,ad_user_data:m,ad_personalization:m,analytics_storage:a,functionality_storage:'granted',security_storage:'granted',wait_for_update:500});}catch(e){gtag('consent','default',{ad_storage:'denied',ad_user_data:'denied',ad_personalization:'denied',analytics_storage:'denied',functionality_storage:'granted',security_storage:'granted',wait_for_update:500});}`,
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "LocalBusiness",
          "@id": `${BASE_URL}/#organization`,
          name: "SimpleTech",
          url: BASE_URL,
          logo: `${BASE_URL}/simpletech-logo.png`,
          image: `${BASE_URL}/simpletech-logo.png`,
          telephone: "+1-909-255-9112",
          email: "contact@simpletechhelper.com",
          priceRange: "$$",
          sameAs: [
            "https://www.facebook.com/simplifyingtechnologyforeveryone",
            "https://instagram.com/simplifying_technology",
            "https://www.linkedin.com/company/simpletechhelper/",
            "https://maps.google.com/?cid=2812500764611667240",
          ],
          address: {
            "@type": "PostalAddress",
            streetAddress: "8880 N Benson Ave #121",
            addressLocality: "Montclair",
            addressRegion: "CA",
            postalCode: "91763",
            addressCountry: "US",
          },
          geo: {
            "@type": "GeoCoordinates",
            latitude: 34.093129,
            longitude: -117.681465,
          },
          areaServed: [
            "Montclair",
            "Claremont",
            "Upland",
            "Ontario",
            "Rancho Cucamonga",
            "Pomona",
            "Chino",
          ].map((city) => ({
            "@type": "City",
            name: city,
            address: {
              "@type": "PostalAddress",
              addressLocality: city,
              addressRegion: "CA",
              addressCountry: "US",
            },
          })),
          openingHoursSpecification: [
            {
              "@type": "OpeningHoursSpecification",
              dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
              opens: "07:00",
              closes: "17:00",
            },
          ],
          contactPoint: {
            "@type": "ContactPoint",
            telephone: "+1-909-255-9112",
            contactType: "Customer Service",
            availableLanguage: ["English", "Spanish"],
            areaServed: "US",
          },
        }),
      },
    ],

  }),
  shellComponent: RootShell,
  component: RootComponent,
  notFoundComponent: NotFoundComponent,
  errorComponent: ErrorComponent,
});

function RootShell({ children }: { children: React.ReactNode }) {
  return (
    <html lang="en">
      <head>
        <HeadContent />
        <style dangerouslySetInnerHTML={{ __html: appCss }} />
      </head>
      <body>
        <a href="#main-content" className="skip-link">
          Skip to main content
        </a>
        {children}

        <Scripts />
      </body>
    </html>
  );
}

import { MobileContactBar } from "@/components/MobileContactBar";
import { AuthProvider } from "@/lib/auth";
import { Toaster } from "@/components/ui/sonner";
const CookieConsent = lazy(() =>
  import("@/components/CookieConsent").then((m) => ({ default: m.CookieConsent }))
);

const COOKIE_CONSENT_KEY = "simpletech-cookie-consent-v1";

import { loadGA, trackPageview, hasAnalyticsConsent, loadClarity, initClickTracking } from "@/lib/analytics";

function RootComponent() {
  const { queryClient } = Route.useRouteContext();
  const router = useRouter();
  const [showA11y, setShowA11y] = useState(false);
  const [showCookies, setShowCookies] = useState(false);
  // reCAPTCHA is loaded on-demand from the /contact route only — keeping it
  // out of the global root saves ~750KB of JS and ~1.7s of main-thread time
  // on every other page.

  useEffect(() => {
    const w = window as Window & { requestIdleCallback?: (cb: () => void) => number };
    const id = w.requestIdleCallback
      ? w.requestIdleCallback(() => setShowA11y(true))
      : window.setTimeout(() => setShowA11y(true), 2000);
    return () => {
      const w2 = window as Window & { cancelIdleCallback?: (id: number) => void };
      if (w2.cancelIdleCallback) w2.cancelIdleCallback(id as number);
      else clearTimeout(id as number);
    };
  }, []);

  // Cookie banner: skip entirely if already answered. Otherwise mount during
  // idle time (well after LCP/TBT) so the consent UI doesn't show up in
  // Lighthouse's measurement window.
  useEffect(() => {
    try {
      if (localStorage.getItem(COOKIE_CONSENT_KEY)) return;
    } catch { /* ignore */ }
    const w = window as Window & { requestIdleCallback?: (cb: () => void) => number };
    const id = w.requestIdleCallback
      ? w.requestIdleCallback(() => setShowCookies(true))
      : window.setTimeout(() => setShowCookies(true), 2500);
    return () => {
      const w2 = window as Window & { cancelIdleCallback?: (id: number) => void };
      if (w2.cancelIdleCallback) w2.cancelIdleCallback(id as number);
      else clearTimeout(id as number);
    };
  }, []);

  // Load GA4 immediately with Consent Mode v2 defaults (denied unless the
  // user previously accepted). The cookie banner upgrades consent on accept.
  useEffect(() => {
    const init = () => {
      loadGA();
      loadClarity();
      initClickTracking();
      trackPageview(router.state.location.pathname);
    };
    const w = window as Window & { requestIdleCallback?: (cb: () => void) => number };
    const id = w.requestIdleCallback
      ? w.requestIdleCallback(init)
      : window.setTimeout(init, 1500);
    return () => {
      const w2 = window as Window & { cancelIdleCallback?: (id: number) => void };
      if (w2.cancelIdleCallback) w2.cancelIdleCallback(id as number);
      else clearTimeout(id as number);
    };
  }, [router]);

  // Track route changes
  useEffect(() => {
    const unsub = router.subscribe("onResolved", ({ toLocation }) => {
      trackPageview(toLocation.pathname);
    });
    return unsub;
  }, [router]);


  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <div className="pb-16 md:pb-0">
          <Outlet />
        </div>
        {showA11y && (
          <Suspense fallback={null}>
            <AccessibilityWidget />
          </Suspense>
        )}
        <MobileContactBar />
        <Toaster />
        {showCookies && (
          <Suspense fallback={null}>
            <CookieConsent />
          </Suspense>
        )}

      </AuthProvider>
    </QueryClientProvider>
  );
}
